/**
 * useAnnouncer Hook
 *
 * Hook para anunciar mensajes a screen readers usando regiones aria-live dinámicas.
 * Útil para notificar acciones importantes sin interrumpir el flujo del usuario.
 *
 * @example
 * ```tsx
 * const { announce } = useAnnouncer();
 *
 * const handleSave = async () => {
 *   try {
 *     await saveData();
 *     announce('Datos guardados exitosamente', 'polite');
 *   } catch (error) {
 *     announce('Error al guardar datos', 'assertive');
 *   }
 * };
 * ```
 */

export interface UseAnnouncerReturn {
  /**
   * Anuncia un mensaje a screen readers
   * @param message - Mensaje a anunciar
   * @param priority - 'polite' (espera turno) o 'assertive' (interrumpe)
   */
  announce: (message: string, priority?: 'polite' | 'assertive') => void;
}

export function useAnnouncer(): UseAnnouncerReturn {
  const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    // Crear elemento de anuncio dinámico
    const announcer = document.createElement('div');

    // Configurar atributos ARIA
    announcer.setAttribute('role', 'status');
    announcer.setAttribute('aria-live', priority);
    announcer.setAttribute('aria-atomic', 'true');

    // Ocultar visualmente pero mantener accesible para screen readers
    announcer.className = 'sr-only';
    announcer.textContent = message;

    // Agregar al DOM
    document.body.appendChild(announcer);

    // Remover después de 1 segundo (el screen reader ya habrá leído el mensaje)
    setTimeout(() => {
      if (document.body.contains(announcer)) {
        document.body.removeChild(announcer);
      }
    }, 1000);
  };

  return { announce };
}
